<script setup lang="ts">

// @ts-nocheck

import { PencilLineIcon, Trash2Icon } from "lucide-vue-next";
import {DialogTrigger} from "reka-ui";
import {computed, type Ref, ref, watch} from "vue";
import type {AddressResponse} from "../../../responses/AddressResponse";
import Modal from "../../Components/Modal.vue";
import {label} from "../../mixins/translations";
import {useAddressStore} from "../../stores/address";
import AddressEntityInput from "./AddressEntityInput.vue";

interface Props {
    current?: AddressResponse,
    countries?: { [key: string]: string },
    showEdit?: boolean,
    showDelete?: boolean
    showSelect?: boolean

}

const props = withDefaults(defineProps<Props>(), {
    current: {},
    countries: [],
    showEdit: true,
    showDelete: true,
    showSelect: false
})

const addressStore = useAddressStore()
const address = ref(props.current);
const errors = ref({});


const emit = defineEmits<{
    (e: 'delete', value: number): void
    (e: 'select', value: AddressResponse): void
}>()

const updateAddress = async (newValue) => {
    address.value = newValue;
}
const saveAddress = async (address) => {
    const response = await addressStore.updateAddress(address.id, address);
    if(response.message === 'success') {
        open.value = false;
    } else {
        errors.value = response.errors;
    }
}

const deleteAddress = async (address) => {
    const response = await addressStore.deleteAddress(address.id);
    if(response.message === 'success') {
        openDelete.value = false;
        emit('delete', address.id);
    } else {
        console.error('Something went wrong deleting the address.')
    }
}

const open: Ref<boolean> = ref(false);
const openDelete: Ref<boolean> = ref(false);

watch(() => props.current, (newValue, _oldValue) => {
   address.value = newValue;
})

const salutation = computed(()=> {
    return address.value.salutation !== 'other'? label(`site.account.salutation.${address.value.salutation}`) : ''
})

const select = () => {
    emit('select', address.value);
}

</script>

<template>
    <div class="border rounded-md p-4 min-h-fit relative text-slate-700" @click.prevent="select">
        <div  v-if="showSelect" class="absolute top-2 right-2 w-8 h-8 flex items-center justify-center">
            <pencil-line-icon class="h-4 w-4 flex"></pencil-line-icon>
        </div>
        <Modal v-if="showDelete" v-model:open="openDelete">
            <template #trigger-wrapper>
                <div class="relative">
                    <DialogTrigger @click.stop class="absolute right-0 top-0 h-8 w-8 rounded-xs flex items-center justify-center cursor-pointer text-end text-sm text-red-600 border border-red-200 hover:bg-red-600 hover:text-white">
                        <Trash2Icon class="w-4 h-4 flex"></Trash2Icon>
                    </DialogTrigger>
                </div>
            </template>
            <template #title>{{ label('site.address.delete.title')}}</template>
            <div class="flex gap-2">
            <button type="button" class="bg-error-500 hover:bg-error-700 text-primary-foreground px-5 pointer rounded-xl flex items-center justify-center h-12 " @click.prevent="deleteAddress(address)">{{ label('site.button.delete')}}</button>
            <button type="button" class="h-12 px-5 pointer rounded-xl flex items-center justify-center text-muted-foreground bg-secondary-100 hover:bg-secondary-200 transition-all duration-500" @click.prevent="openDelete = false">{{ label('site.button.cancel')}}</button>
            </div>
        </Modal>
        <div class="w-full">{{ address.company }}</div>
        <div class="w-full">
<!--            <span v-html="salutation"></span>-->
            {{ address.first_name }}
            {{ address.insertion }} {{ address.last_name }}
        </div>
        <div class="w-full">{{ address.address_line_1 }}</div>
        <div class="w-full">{{ address.address_line_2 }}</div>
        <div class="w-full">{{ address.street }} {{ address.house_number }}
            {{ address.house_number_addition }}
        </div>
        <div class="w-full">{{ address.postal }} {{ address.city }}</div>
        <div class="w-full">{{ countries[address.country] ?? '' }}</div>
        <Modal v-if="showEdit" v-model:open="open">
            <template #trigger-wrapper class="text-right">
                <div class="relative">
                <DialogTrigger @click.stop class="absolute right-0 bottom-0 cursor-pointer text-end text-sm text-gray-600 font-semibold">
                    {{ label('site.address.edit') }}
                </DialogTrigger>
                </div>
            </template>
            <template #title>{{ label('site.address.edit')}}</template>
            <address-entity-input class="min-h-12" :errors="errors" :model-value="address" @update:model-value="value => updateAddress(value)" :countries="countries" :show-inputs="true" :show-address-inputs="true" :hide-preview="true">
                <template #buttons>
                    <button type="button" class="h-12 px-5 pointer rounded-xl flex items-center justify-center bg-primary hover:bg-primary-300 text-white font-semibold transition-all duration-500 " @click.prevent="saveAddress(address)">
                        {{ label('site.button.edit') }}
                    </button>
                </template>
            </address-entity-input>
        </Modal>
    </div>
</template>
